// TypeScript types matching MedNec backend view models

// ── Case ───────────────────────────────────────────────────────────────

export interface PatientView {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  memberId: string;
  middleInitial?: string | null;
  sex?: string | null;
  ssn4?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  homePhone?: string | null;
  cellPhone?: string | null;
}

export interface IcdCodeView {
  value: string;
  description?: string | null;
}

export interface AuditTrailEntryView {
  fromStatus: string;
  toStatus: string;
  reason: string;
  userId: string;
  occurredAt: string;
}

// ── Scheduling packet value objects ────────────────────────────────

export interface InsuranceDetail {
  policyNumber?: string | null;
  groupNumber?: string | null;
  preAuthNumber?: string | null;
  wcClaimNumber?: string | null;
  relationshipToPatient?: string | null;
  insuranceNotes?: string | null;
}

export interface SurgeryInfo {
  surgeryDate?: string | null;
  surgeryTime?: string | null;
  duration?: string | null;
  position?: string | null;
  anesthesiaType?: string | null;
  equipment: string[];
  implantVendor?: string | null;
  implantRepInfo?: string | null;
  implantsRequired: boolean;
  specialInstructions?: string | null;
}

export interface GuarantorInfo {
  sameAsPatient: boolean;
  name?: string | null;
  relationship?: string | null;
  dateOfBirth?: string | null;
}

export interface PreopInfo {
  nkda: boolean;
  allergies: string[];
  antibiotics: AntibioticOrder[];
  scd: boolean;
  tedHose: boolean;
  ivStart: boolean;
  pregnancyTest: boolean;
  otherOrders?: string | null;
}

export interface AntibioticOrder {
  drug: string;
  dose: string;
  timing: string;
}

export interface ProcedureDetailView {
  cptCode: string;
  description?: string | null;
  level?: string | null;
  approach?: string | null;
  side?: string | null;
  isPrimary: boolean;
}

export interface MedNecCaseView {
  id: string;
  patient: PatientView | null;
  payerId: string;
  payerName: string;
  facilityCode: string;
  physicianName: string;
  physicianNpi: string;
  physicianGroup?: string | null;
  cptCodes: string[];
  icdCodes: IcdCodeView[];
  status: string;
  encounterId: string | null;
  notes: string | null;
  documentCount: number;
  createdAt: string;
  submittedAt: string | null;
  auditTrail: AuditTrailEntryView[];
  nurseOverrideReason: string | null;
  levelOfCare: string | null;
  isDirectAdmit: boolean;
  // Scheduling packet extended fields
  insurance?: InsuranceDetail | null;
  surgery?: SurgeryInfo | null;
  guarantor?: GuarantorInfo | null;
  preop?: PreopInfo | null;
  procedures?: ProcedureDetailView[] | null;
  fullTextBag?: string | null;
  imagingSummary?: string | null;
}

export function createEmptyCase(overrides?: Partial<MedNecCaseView>): MedNecCaseView {
  return {
    id: '',
    patient: { firstName: '', lastName: '', dateOfBirth: '', memberId: '' },
    payerId: '',
    payerName: '',
    facilityCode: '',
    physicianName: '',
    physicianNpi: '',
    physicianGroup: null,
    cptCodes: [],
    icdCodes: [] as IcdCodeView[],
    status: '',
    encounterId: null,
    notes: null,
    documentCount: 0,
    createdAt: '',
    submittedAt: null,
    auditTrail: [],
    nurseOverrideReason: null,
    levelOfCare: null,
    isDirectAdmit: false,
    insurance: { policyNumber: null, groupNumber: null, preAuthNumber: null, wcClaimNumber: null, relationshipToPatient: null, insuranceNotes: null },
    surgery: { surgeryDate: null, surgeryTime: null, duration: null, position: null, anesthesiaType: null, equipment: [], implantVendor: null, implantRepInfo: null, implantsRequired: false, specialInstructions: null },
    guarantor: { sameAsPatient: false, name: null, relationship: null, dateOfBirth: null },
    preop: { nkda: false, allergies: [], antibiotics: [], scd: false, tedHose: false, ivStart: false, pregnancyTest: false, otherOrders: null },
    procedures: [],
    fullTextBag: null,
    imagingSummary: null,
    ...overrides,
  };
}

// ── Documents ──────────────────────────────────────────────────────────

export interface DocumentView {
  documentId: string;
  documentTypes: string[];
  fileName: string;
  extension: string;
  uploadedAt: string;
  uploadedBy: string;
}

export interface CaseDocumentsView {
  caseId: string;
  status: string;
  documents: DocumentView[];
}

export interface DocumentUrlView {
  documentId: string;
  fileName: string;
  sasUrl: string;
  expiresAt: string;
}

// ── Dashboard ──────────────────────────────────────────────────────────

export interface DashboardSummaryView {
  statusCounts: Record<string, number>;
  total: number;
  facility: string | null;
}

// ── Gap Analysis ───────────────────────────────────────────────────────

export interface CheckableItemView {
  text: string;
  isChecked: boolean;
}

export interface GapCriterionView {
  description: string;
  status: string;
  confidence: number;
  policySectionReference: string;
  evaluationReasoning: string;
  evidenceQuotes: string[];
  gapComments: string | null;
  actionItems: CheckableItemView[];
  subCriteria: GapCriterionView[];
  requiredDocuments: string[];
  nurseComment: string | null;
  isManual: boolean;
}

export interface InpatientAnalysisView {
  recommendation: string;
  inpatientCriteriaMet: string[];
  inpatientCriteriaGaps: string[];
  additionalDocumentationNeeded: string[];
  samplePhysicianStatement: string;
  confidence: number;
}

export interface EmergentAnalysisView {
  recommendation: string;
  emergentCriteriaMet: string[];
  emergentCriteriaGaps: string[];
  additionalDocumentationNeeded: string[];
  samplePhysicianStatement: string;
  confidence: number;
}

export interface GapAnalysisView {
  policyNumber: string;
  policyName: string;
  policyEffectiveDate: string | null;
  overallRiskStatus: string;
  overallConfidence: number;
  documentsReviewed: string[];
  criteria: GapCriterionView[];
  inpatientAnalysis: InpatientAnalysisView | null;
  emergentAnalysis: EmergentAnalysisView | null;
  preAuthChecklist: CheckableItemView[];
  actionItemsSummary: CheckableItemView[];
  analyzedAt: string;
}

export type GapAnalysisRunStatus = 'NotStarted' | 'Running' | 'Completed' | 'Failed';

export interface GapAnalysisStatusView {
  runStatus: GapAnalysisRunStatus;
  runError?: string | null;
  runStartedAt?: string | null;
  analysis?: GapAnalysisView | null;
}

// ── Ingestion ──────────────────────────────────────────────────────────

export interface IngestionResultView {
  success: boolean;
  errorMessage?: string | null;
  caseId?: string | null;
  patientName?: string | null;
  calcType?: string | null;
  procedureCount: number;
  diagnosisCount: number;
  documentCount: number;
}
